import { Roles, UserList } from '@common/types/user';
import { GenericActionDialog } from '@components/display/dialogs';
import { BulkActionOption } from '@components/utility/selects';
import { useAppDispatch } from '@hooks/generic';
import { useAuth } from '@hooks/useAuth';
import { useDebounce } from '@hooks/useDebounce';
import { useLazyDownload } from '@hooks/useDownload';
import { CreateMailchimpAudienceDialog } from '@members/routes/MembersOverview/CreateMailchimpAudienceDialog';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import { alpha, Box, SelectChangeEvent } from '@mui/material';
import {
    useDeleteAccountMutation,
    useGetAccountsQuery,
    useGetSalesManagersQuery,
    useLazyGetAccountsWithQuerysetIDsQuery,
} from '@services/accounts';
import { useGetCountriesQuery } from '@services/countries';
import { useGetRolesQuery } from '@services/userRoles';
import { setTitle } from '@store/appSlice';
import { handleError, toaster } from '@utils/common';
import { isVictronAdmin } from '@utils/user';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MembersFilters } from '../../components/MembersFilters';
import { MembersTable } from '../../components/MembersTable';
import { getMemberOrderingFields, memberTableHeaders } from '../../constants';

const NON_FILTER_PARAMS = ['search', 'sort', 'page_size', 'page'];
const ROWS_PER_PAGE_OPTIONS = [10, 20, 50, 100];
const MEMBER_FILTER_FIELDS = ['role', 'sales_manager', 'country'];

export const MembersOverview = () => {
    const dispatch = useAppDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const [isCreateMailchimpAudienceDialogOpen, setIsCreateMailchimpAudienceDialogOpen] = useState(false);
    const [markedUserToDelete, setMarkedUserToDelete] = useState<UserList | null>(null);
    const { user } = useAuth();

    const search = searchParams.get('search');
    const debouncedSearch = useDebounce({ value: search, delay: 500 });
    const requestedSort = searchParams.get('sort');
    const requestedPage = Number(searchParams.get('page') ?? 1);
    const page = Number.isInteger(requestedPage) ? Math.max(requestedPage, 1) : 1;
    const requestedRowsPerPage = Number(searchParams.get('page_size') ?? ROWS_PER_PAGE_OPTIONS[0]);
    const rowsPerPage =
        ROWS_PER_PAGE_OPTIONS.find((option) => option === requestedRowsPerPage) ?? ROWS_PER_PAGE_OPTIONS[0];
    const [selectedRows, setSelectedRows] = useState<number[]>([]);

    const { data: roles, isLoading: areRolesLoading } = useGetRolesQuery();
    const { data: salesManagers, isLoading: areSalesManagerLoading } = useGetSalesManagersQuery(undefined, {
        selectFromResult: (response) => ({
            ...response,
            data: response.data?.map((manager) => ({ label: manager.full_name, value: manager.id.toString() })) ?? [],
        }),
    });
    const { data: countries, isLoading: areCountriesLoading } = useGetCountriesQuery();

    const [exportCSV, { isLoading: isExportingCSV }] = useLazyDownload();
    const [deleteAccount, { isLoading: isDeletingAccount }] = useDeleteAccountMutation();

    const filters = useMemo(
        () =>
            Array.from(searchParams.entries())
                .filter(([field, value]) => MEMBER_FILTER_FIELDS.includes(field) && value !== '')
                .reduce((acc, [field, value]) => {
                    if (
                        (field === 'role' && !roles?.some((role) => role.value === value)) ||
                        (field === 'sales_manager' && !salesManagers?.some((manager) => manager.value === value)) ||
                        (field === 'country' && !countries?.some((country) => country.value === value))
                    ) {
                        return acc;
                    }

                    acc[field] ??= [];
                    acc[field].push(value);
                    return acc;
                }, {} as Record<string, string[]>),
        [countries, roles, salesManagers, searchParams]
    );

    const filtersNeedNormalization = useMemo(
        () =>
            MEMBER_FILTER_FIELDS.some((field) => {
                const validValues = filters[field] ?? [];
                const currentValues = searchParams.getAll(field);

                return (
                    currentValues.length !== validValues.length ||
                    currentValues.some((value, index) => value !== validValues[index])
                );
            }),
        [filters, searchParams]
    );

    const {
        data: accounts,
        isLoading: areAccountsLoading,
        isFetching: areAccountsFetching,
    } = useGetAccountsQuery({
        page,
        page_size: rowsPerPage,
        search: debouncedSearch ?? undefined,
        ordering: getMemberOrderingFields(requestedSort),
        role: filters.role ? (filters.role as Roles[]) : undefined,
        sales_manager: filters.sales_manager?.map(Number) ?? undefined,
        country: filters.country ?? undefined,
    });
    const [getAccountsWithQuerysetIDs, { isFetching: areAccountIDsFetching }] =
        useLazyGetAccountsWithQuerysetIDsQuery();
    const maxPage = Math.max(1, accounts?.count ? Math.ceil(accounts?.count / rowsPerPage) : 1);
    const normalizedPage = Math.min(page, maxPage);
    const sortColumn = requestedSort?.replace(/^-/, '');
    const sort =
        requestedSort && sortColumn && memberTableHeaders.some((header) => header.id === sortColumn)
            ? requestedSort
            : null;

    useEffect(() => {
        if (!accounts || (normalizedPage === page && rowsPerPage === requestedRowsPerPage && sort === requestedSort)) {
            return;
        }

        setSearchParams((previousParams) => {
            const nextParams = new URLSearchParams(previousParams);
            nextParams.set('page', normalizedPage.toString());
            nextParams.set('page_size', rowsPerPage.toString());

            if (sort) {
                nextParams.set('sort', sort);
            } else {
                nextParams.delete('sort');
            }

            return nextParams;
        });
    }, [accounts, normalizedPage, page, requestedRowsPerPage, requestedSort, rowsPerPage, setSearchParams, sort]);

    useEffect(() => {
        if (areRolesLoading || areSalesManagerLoading || areCountriesLoading || !filtersNeedNormalization) {
            return;
        }

        setSearchParams((previousParams) => {
            const nextParams = new URLSearchParams(previousParams);

            MEMBER_FILTER_FIELDS.forEach((field) => {
                const validValues = filters[field] ?? [];
                nextParams.delete(field);
                validValues.forEach((value) => nextParams.append(field, value));
            });

            return nextParams;
        });
    }, [
        areCountriesLoading,
        areRolesLoading,
        areSalesManagerLoading,
        filters,
        filtersNeedNormalization,
        setSearchParams,
    ]);

    useEffect(() => {
        dispatch(setTitle('Members Overview - Dashboard'));
    }, [dispatch]);

    const handleExportCSV = async () => {
        try {
            if (selectedRows.length === 0) {
                const params = new URLSearchParams();
                params.append('search', search ?? '');
                MEMBER_FILTER_FIELDS.forEach((field) => {
                    const values = filters[field] ?? [];
                    values.forEach((value) => params.append(field, value));
                });
                await exportCSV({
                    relativePath: `management/accounts/export/csv/?${params.toString()}`,
                    method: 'GET',
                });
            } else {
                await exportCSV({
                    relativePath: `management/accounts/export/csv/`,
                    body: {
                        user_ids: selectedRows,
                    },
                    method: 'POST',
                });
            }
        } catch (error) {
            handleError('Error exporting CSV', error);
        }
    };

    const handleDeleteAccount = async () => {
        try {
            if (!markedUserToDelete?.id) {
                return;
            }
            await deleteAccount({ user_id: markedUserToDelete.id }).unwrap();
            setMarkedUserToDelete(null);
            toaster.success({
                title: 'Member Deleted Successfully',
                msg: `${
                    markedUserToDelete?.first_name && markedUserToDelete?.last_name
                        ? `${markedUserToDelete?.first_name} ${markedUserToDelete?.last_name}`
                        : markedUserToDelete?.full_name
                } successfully deleted.`,
            });
            setSelectedRows((prevSelectedRows) => prevSelectedRows.filter((id) => id !== markedUserToDelete?.id));
        } catch (error) {
            handleError('Error deleting account', error);
        }
    };

    const bulkOptions: BulkActionOption[] = useMemo(
        () => [
            {
                label: 'Create Mailchimp Audience',
                onClick: () => setIsCreateMailchimpAudienceDialogOpen(true),
            },
            { label: 'Export CSV', isLoading: isExportingCSV, onClick: handleExportCSV },
        ],
        [filters, isExportingCSV, handleExportCSV, setIsCreateMailchimpAudienceDialogOpen]
    );

    const updateSearchParams = (update: (params: URLSearchParams) => void, resetPage = true) => {
        setSearchParams((previousParams) => {
            const nextParams = new URLSearchParams(previousParams);
            update(nextParams);
            if (resetPage) {
                nextParams.set('page', '1');
            }
            return nextParams;
        });
    };

    const handleFilterChange = (field: string, event: SelectChangeEvent<unknown>) => {
        updateSearchParams((params) => {
            params.delete(field);
            (event.target.value as string[]).forEach((value) => params.append(field, value));
        });
    };

    const handleFilterRemove = (field: string, value: string) => {
        updateSearchParams((params) => {
            const values = params.getAll(field).filter((currentValue) => currentValue !== value);
            params.delete(field);
            values.forEach((currentValue) => params.append(field, currentValue));
        });
    };

    const handleClearFilters = () => {
        updateSearchParams((params) => {
            Array.from(params.keys()).forEach((key) => {
                if (!NON_FILTER_PARAMS.includes(key)) {
                    params.delete(key);
                }
            });
        });
    };

    const handleSortClick = (column: string) => {
        if (!memberTableHeaders.some((header) => header.id === column)) {
            return;
        }

        const currentColumn = sort?.replace(/^-/, '');
        const nextSort = currentColumn !== column ? column : sort?.startsWith('-') ? undefined : `-${column}`;

        updateSearchParams((params) => {
            if (nextSort) {
                params.set('sort', nextSort);
            } else {
                params.delete('sort');
            }
        });
    };

    const handleSelectAll = async (checked: boolean) => {
        if (!checked) {
            setSelectedRows([]);
            return;
        }

        try {
            const response = await getAccountsWithQuerysetIDs({
                page,
                page_size: rowsPerPage,
                search: debouncedSearch ?? undefined,
                ordering: getMemberOrderingFields(requestedSort),
                role: filters.role ? (filters.role as Roles[]) : undefined,
                sales_manager: filters.sales_manager?.map(Number) ?? undefined,
                country: filters.country ?? undefined,
                include_queryset_ids: true,
            }).unwrap();
            setSelectedRows(response.queryset_ids ?? []);
        } catch (error) {
            handleError('Error selecting members', error);
        }
    };

    const getChipLabel = (filter: string, item: string) => {
        switch (filter) {
            case 'country':
                return countries?.find((country) => country.value === item)?.label ?? item;
            case 'role':
                return roles?.find((role) => role.value === item)?.label ?? item;
            case 'sales_manager':
                return salesManagers?.find((manager) => manager.value.toString() === item)?.label ?? item;
            default:
                return item;
        }
    };

    const isAdmin = user ? isVictronAdmin(user) : false;

    return (
        <Box component="main" py="25px" px="62px">
            <Box bgcolor="white.main" borderRadius="14px" border={1} borderColor={alpha('#000', 0.1)} p="44px">
                <MembersFilters
                    search={search ?? ''}
                    filters={filters}
                    roles={roles ?? []}
                    salesManagers={salesManagers}
                    countries={countries ?? []}
                    areRolesLoading={areRolesLoading}
                    areSalesManagerLoading={areSalesManagerLoading}
                    areCountriesLoading={areCountriesLoading}
                    bulkOptions={bulkOptions}
                    bulkActionsDisabled={!isAdmin}
                    bulkActionsTooltip={user && isAdmin ? '' : 'You must be a Victron admin to perform this action'}
                    selectedCount={selectedRows.length}
                    onSearchChange={(value) =>
                        updateSearchParams((params) => {
                            params.set('search', value);
                        })
                    }
                    onSearchClear={() =>
                        updateSearchParams((params) => {
                            params.delete('search');
                        })
                    }
                    onFilterChange={handleFilterChange}
                    onFilterRemove={handleFilterRemove}
                    onClearFilters={handleClearFilters}
                    getChipLabel={getChipLabel}
                />
                <MembersTable
                    members={accounts?.results ?? []}
                    sort={sort}
                    page={normalizedPage}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS.map((value) => ({ label: String(value), value }))}
                    totalCount={accounts?.count ?? 0}
                    selectedRows={selectedRows}
                    isAdmin={isAdmin}
                    onSortClick={handleSortClick}
                    onPageChange={(nextPage) =>
                        updateSearchParams((params) => {
                            params.set('page', Math.min(Math.max(nextPage, 1), maxPage).toString());
                        }, false)
                    }
                    onRowsPerPageChange={(nextRows) =>
                        updateSearchParams((params) => {
                            params.set('page_size', nextRows.toString());
                        })
                    }
                    onSelectAll={handleSelectAll}
                    onRowCheck={(member) =>
                        setSelectedRows((currentRows) =>
                            currentRows.includes(member.id)
                                ? currentRows.filter((rowId) => rowId !== member.id)
                                : [...currentRows, member.id]
                        )
                    }
                    loading={areAccountsLoading || areAccountsFetching}
                    isSelectAllLoading={areAccountIDsFetching}
                    onRowOptionClick={(member) => {
                        setMarkedUserToDelete(member);
                    }}
                />
                <CreateMailchimpAudienceDialog
                    open={isCreateMailchimpAudienceDialogOpen}
                    onClose={() => setIsCreateMailchimpAudienceDialogOpen(false)}
                    search={search}
                    filters={filters}
                    user_ids={selectedRows}
                />
                <GenericActionDialog
                    open={!!markedUserToDelete}
                    onClose={() => {
                        setMarkedUserToDelete(null);
                    }}
                    keepMounted
                    icon={<PersonOutlinedIcon color="primary" sx={{ fontSize: 30 }} />}
                    title="Delete Member?"
                    content={
                        <>
                            Delete{' '}
                            <b>
                                {markedUserToDelete?.first_name && markedUserToDelete?.last_name
                                    ? `${markedUserToDelete?.first_name} ${markedUserToDelete?.last_name}`
                                    : markedUserToDelete?.full_name ?? 'this member'}
                            </b>
                            ? This action is <b>permanent</b> and cannot be undone.
                        </>
                    }
                    buttons={[
                        {
                            label: 'Delete',
                            buttonProps: {
                                color: 'signal-warning',
                                onClick: () => handleDeleteAccount(),
                                loading: isDeletingAccount,
                                loadingPosition: 'start',
                            },
                        },
                        {
                            label: 'Cancel',
                            buttonProps: {
                                variant: 'outlined',
                                color: 'neutral-800',
                                onClick: () => setMarkedUserToDelete(null),
                                disabled: isDeletingAccount,
                            },
                        },
                    ]}
                />
            </Box>
        </Box>
    );
};
